import { FunctionComponent } from "react";
import { Tag, Tooltip } from "antd";
import {
    CheckCircleTwoTone,
    ExclamationCircleTwoTone,
    PauseCircleTwoTone,
    StopOutlined,
} from "@ant-design/icons";
import { StatutReglement } from "../../services/paiement";
import { formatMontant, getResteAPayer, getStatutReglement } from "./paiementUtils";

const STATUT_REGLEMENT_CONFIG: Record<StatutReglement, { label: string; icon: JSX.Element; tagColor: string }> = {
    [StatutReglement.SOLDE]: {
        label: "Soldé",
        icon: <CheckCircleTwoTone twoToneColor="#52c41a" />,
        tagColor: "success",
    },
    [StatutReglement.PARTIEL]: {
        label: "Partiel",
        icon: <PauseCircleTwoTone twoToneColor="#faad14" />,
        tagColor: "warning",
    },
    [StatutReglement.NON_REGLE]: {
        label: "Non réglé",
        icon: <StopOutlined style={{ color: "#8c8c8c" }} />,
        tagColor: "default",
    },
    [StatutReglement.TROP_PERCU]: {
        label: "Trop-perçu",
        icon: <ExclamationCircleTwoTone twoToneColor="#ff4d4f" />,
        tagColor: "error",
    },
};

export const getLibelleStatutReglement = (statut: StatutReglement): string =>
    STATUT_REGLEMENT_CONFIG[statut].label;

export type PaiementBadgeProps = {
    montantTotal?: number | null;
    montantRegle?: number | null;
    /** Affiche « 120,00 € / 200,00 € » sous le tag. */
    detaille?: boolean;
};

/**
 * État de règlement d'une inscription, dérivé des deux montants plutôt que reçu du serveur.
 * Même règle que côté back (StatutReglementEnum.of), cf. getStatutReglement.
 */
export const PaiementBadge: FunctionComponent<PaiementBadgeProps> = ({ montantTotal, montantRegle, detaille = true }) => {
    const statut = getStatutReglement(montantTotal, montantRegle);
    const config = STATUT_REGLEMENT_CONFIG[statut];
    const reste = getResteAPayer(montantTotal, montantRegle);

    const infobulle = statut === StatutReglement.TROP_PERCU
        ? `Trop-perçu de ${formatMontant(Math.abs(reste))} — le tarif a probablement été revu à la baisse après encaissement`
        : `Reste à payer : ${formatMontant(reste)}`;

    return (
        <Tooltip title={infobulle}>
            <div style={{ display: "inline-flex", flexDirection: "column", gap: 2, lineHeight: 1.3 }}>
                <Tag
                    icon={config.icon}
                    color={config.tagColor}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, borderRadius: 6, marginInlineEnd: 0 }}
                    {...({ testid: `statut-reglement-${statut}` } as any)}
                >
                    {config.label}
                </Tag>
                {detaille && (
                    <span style={{ fontSize: 11, color: "rgba(0, 0, 0, 0.45)", fontVariantNumeric: "tabular-nums" }}>
                        {formatMontant(montantRegle ?? 0)} / {formatMontant(montantTotal ?? 0)}
                    </span>
                )}
            </div>
        </Tooltip>
    );
};
